import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Flow } from '../components/Diagram';

interface Row {
  key: string;
  name: string;
  blurb: string;
}

const STATUSES: Row[] = [
  {
    key: 'ACCUMULATING',
    name: 'Accumulating',
    blurb:
      'API capture. The period has closed but the grace window has not expired, so late events still count towards it. Nothing to do — it seals itself.',
  },
  {
    key: 'AWAITING',
    name: 'Awaiting',
    blurb:
      'Manual capture, no value entered yet. Waiting on a person. Revvly raises a task, and the reading is reconsidered on every sweep, so a late entry resolves it without anyone reopening anything.',
  },
  {
    key: 'UNVERIFIED',
    name: 'Unverified',
    blurb:
      'API capture, and the grace window expired having received nothing at all. An integration that broke and a customer who used nothing look identical from the counter, so Revvly refuses to guess: it never bills this as zero on its own.',
  },
  {
    key: 'SEALED',
    name: 'Sealed',
    blurb:
      'The billable quantity is final. Releases the invoice. A sealed reading records where its number came from — the integration, an operator, an operator overriding a quiet integration, or the zero policy below.',
  },
  {
    key: 'WAIVED',
    name: 'Waived',
    blurb:
      'You decided not to bill this period, with a reason on the record. Releases the invoice and charges nothing for it.',
  },
];

const AXES: Row[] = [
  {
    key: 'PER_PERIOD',
    name: 'Resets each period',
    blurb:
      'Consumption that belongs to its period and dies with it — API calls, tokens, transactions. The total is only final once the period has closed, so these are the components that produce a reading and gate an invoice. They must bill in arrears: there is nothing to charge at the moment a period opens.',
  },
  {
    key: 'STANDING',
    name: 'Carries forward',
    blurb:
      'A standing level that changes only when something changes it — seats, licences, a storage high-water mark. Rebaselined at the boundary rather than zeroed, so nothing here waits on a reading and nothing here holds an invoice.',
  },
];

const BILLED: Row[] = [
  { key: 'MAX', name: 'Highest observed', blurb: 'The peak reached during the period.' },
  { key: 'TOTAL', name: 'Total over the period', blurb: 'Everything accumulated across it.' },
  {
    key: 'CLOSING',
    name: 'Value at period end',
    blurb: 'The level standing at the moment the period closed.',
  },
];

export function MeteredUsageGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Capture metered usage — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Capture metered usage</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Measure the period, <em>then</em> bill it.
        </h1>
        <p className="dp-oneliner">
          A metered charge can only be totalled once its period has actually happened. Revvly
          captures that total as a usage reading, holds the invoice until the reading is settled,
          and gives you the surfaces to settle it. A period is never billed on a number nobody
          looked at.
        </p>
      </header>

      <section className="dp-section">
        <h2>Which components this applies to</h2>
        <p className="dp-plain">
          Two independent things decide how a component measures: what happens to its quantity at
          the period boundary, and which number in the period gets rated. Only the first one gates
          an invoice.
        </p>
        <div className="dp-fields-table">
          {AXES.map((a) => (
            <div className="dp-field-row" key={a.key}>
              <div className="dp-field-k">{a.name}</div>
              <div className="dp-field-v">{a.blurb}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Independently of that, you choose which number is rated:
        </p>
        <div className="dp-fields-table">
          {BILLED.map((b) => (
            <div className="dp-field-row" key={b.key}>
              <div className="dp-field-k">{b.name}</div>
              <div className="dp-field-v">{b.blurb}</div>
            </div>
          ))}
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Components authored before these two settings existed were converted from their old
          measurement mode automatically: Peak became a carried-forward quantity billed on its
          highest value, Cumulative became a per-period quantity billed on its total. Nothing
          changed about how they bill.
        </p>
      </section>

      <section className="dp-section">
        <h2>The cycle</h2>
        <ol className="qs-steps">
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>
              1
            </span>
            <div>
              <div className="qs-step-label">Usage accrues</div>
              <div className="qs-step-detail">
                Through the period, an integration posts increments to the usage API, or nobody does
                and an operator will enter the figure later. Which of the two Revvly expects is the
                component&rsquo;s capture mode.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>
              2
            </span>
            <div>
              <div className="qs-step-label">The period closes</div>
              <div className="qs-step-detail">
                A daily sweep captures the accumulated total as a reading before the live counter
                resets, so the number that will be billed is fixed at the boundary rather than read
                back afterwards.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>
              3
            </span>
            <div>
              <div className="qs-step-label">A grace window runs</div>
              <div className="qs-step-detail">
                For an integrated meter the reading stays open for a day after close, so events
                delayed by a queue or a retry still land in the period they belong to.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>
              4
            </span>
            <div>
              <div className="qs-step-label">The reading seals, or asks for you</div>
              <div className="qs-step-detail">
                An hourly sweep settles what it can. A meter that reported seals on its own; one
                that did not is handled by the policy below, and anything left needing a decision
                raises a task.
              </div>
            </div>
          </li>
          <li className="qs-step">
            <span className="qs-phase-num" style={{ marginRight: 12 }}>
              5
            </span>
            <div>
              <div className="qs-step-label">The invoice issues</div>
              <div className="qs-step-detail">
                Once every reading it carries is sealed or waived, the held draft issues: it takes
                its invoice number and its dates at that moment, and enters receivables like any
                other invoice.
              </div>
            </div>
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <h2>Reading states</h2>
        <p className="dp-plain">
          Only <strong>Sealed</strong> and <strong>Waived</strong> release an invoice. Every other
          state holds it.
        </p>
        <Figure
          title="Path of a usage reading"
          caption="Only a sealed or waived reading lets the held invoice issue; every other state keeps it held."
        >
          <Flow
            steps={[
              { title: 'Period closes', text: 'The total is captured as a reading.', tone: 'muted' },
              { fork: [
                { title: 'Accumulating', text: 'API capture, grace window still open.' },
                { title: 'Awaiting', text: 'Manual capture, no value entered yet.' },
              ] },
              { fork: [
                { title: 'Sealed', text: 'Quantity is final.', tone: 'accent' },
                { title: 'Waived', text: 'Not billed, with a reason on record.', tone: 'accent' },
                { title: 'Unverified', text: 'API sent nothing; invoice stays held.', tone: 'warn' },
              ] },
              { title: 'Held invoice issues', text: 'Once every reading is sealed or waived.', tone: 'accent' },
            ]}
          />
        </Figure>
        <div className="dp-fields-table">
          {STATUSES.map((s) => (
            <div className="dp-field-row" key={s.key}>
              <div className="dp-field-k">{s.name}</div>
              <div className="dp-field-v">{s.blurb}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>What silence means, per component</h2>
        <p className="dp-plain">
          A period that received nothing is ambiguous, and the ambiguity is expensive in both
          directions: bill it as zero when an integration was broken and you lose the revenue
          silently; hold it for review when zero is the ordinary outcome and you generate a held
          invoice and a task every period, for every such client, forever. So it is a per-component
          decision — a fact about what is being metered, not about who is being billed.
        </p>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Review (default)</div>
            <div className="dp-field-v">
              Seal the period as Unverified, hold the invoice, raise a task. Right for a meter where
              usage is expected every period. This is what every component does unless you say
              otherwise.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Zero</div>
            <div className="dp-field-v">
              Seal an unreported period at zero and let the invoice go. Right for overage on a plan
              a client rarely exceeds, or a pay-per-incident charge that is empty most months. The
              reading records that its zero came from the policy, not from a person or an
              integration — so the audit trail never claims a measurement that was not made.
            </div>
          </div>
        </div>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Capture mode normally comes from the client: an integrated client is API, everyone else is
          manual. One component on one subscription can be pinned the other way, with a reason kept
          for audit — for the client who integrates most of their meters and reports one by hand.
        </p>
      </section>

      <section className="dp-section">
        <h2>Working the queue</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Bulk usage entry</div>
            <div className="dp-field-v">
              Every open reading across the workspace in one grid, each row showing the client, the
              component, the plan and the subscription. Linked from the client page and the
              Subscriptions tab. This is where a month of manual meters gets worked.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Finance → Awaiting</div>
            <div className="dp-field-v">
              Every held draft invoice, oldest first, with the metered period, the hold reason and
              how long it has been waiting; anything past a week is flagged. Each row links to the
              invoice and to the subscription that will release it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Tasks</div>
            <div className="dp-field-v">
              One task per period that needs a person, with the client attached: a manual meter
              awaiting entry, or an integration that has gone quiet. They are distinct kinds, so the
              two problems do not end up on the same queue.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Correct</div>
            <div className="dp-field-v">
              Supersede a sealed reading with a different quantity and a reason, any time before its
              invoice issues. The original is kept.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Waive</div>
            <div className="dp-field-v">
              Settle a period you have decided not to bill, with a required reason. Releases the
              invoice, charges nothing.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Held drafts are not pending invoices</h2>
        <p className="dp-plain">
          Whether a document has been issued and whether it has been paid are separate questions,
          and a held draft answers only the first. It has no invoice number, no invoice date, no due
          date and no payment status, which is exactly what keeps it out of receivables, out of AR
          aging and out of payment reminders: nothing is owed until it issues. It picks all of those
          up at the moment it issues, so invoice numbers stay in issue order.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Drafting and holding was chosen over declining to invoice at all. An invoice that exists
          and says &ldquo;draft, awaiting usage&rdquo; is far easier to act on than one that
          silently does not exist because a period never sealed. For the same reason an abandoned
          draft is voided rather than deleted — the record that a period was expected to bill and
          did not is what an audit needs.
        </p>
      </section>

      <section className="dp-section">
        <h2>What holds an invoice</h2>
        <ul className="dp-connects">
          <li>A reading on it is not yet sealed or waived</li>
          <li>
            The subscription is contracted to bill a per-period metered component that nothing is
            tracking usage for — the invoice is held with that component named, rather than issued
            without it
          </li>
        </ul>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The second case is the one worth knowing about: an untracked component produces no line at
          all, so without the hold the renewal would issue the base charge alone and the metered
          charge would disappear from the bill in silence.
        </p>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/guides/usage-reporting"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Report usage through REST ›
            </Link>
          </li>
          <li>
            <Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>
              Ingest usage events ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/billing-timing"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Advance vs. arrears billing ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/pricing-models"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Choose a pricing model ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
